import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getApi } from "../services/api";
import { toast } from "react-toastify";
import { AuthContext } from "../context/AuthContext";
import CourseDetailLoading from "../components/common/Loadinganimations/CourseDetail";
import PaymentOptions from "../components/Payment/PaymentOptions";

const CourseDetailPage = () => {
	const { courseId } = useParams();
	const [course, setCourse] = useState(null);
	const [classrooms, setClassrooms] = useState([]);
	const [selectedClassroom, setSelectedClassroom] = useState(""); // State for selected classroom
	const { user } = useContext(AuthContext); // Get user data from auth context
	const [isModalVisible, setModalVisible] = useState(false);

	useEffect(() => {
		const fetchCourseDetails = async () => {
			try {
				const response = await getApi(`/courses/${courseId}`);
				if (response && !response.error) {
					setCourse(response);
				} else {
					console.error(
						"Error fetching course details:",
						response.message || "Unknown error"
					);
				}
			} catch (error) {
				console.error("Error fetching course details:", error.message);
			}
		};

		fetchCourseDetails();
	}, [courseId]);

	console.log(course);

	// Fetch classrooms data
	useEffect(() => {
		const fetchClassrooms = async () => {
			try {
				const response = await getApi(`/classrooms`, {
					filter: JSON.stringify({ courseId }),
				});
				if (response && !response.error) {
					setClassrooms(response);
				} else {
					console.error(
						"Error fetching classrooms:",
						response.message || "Unknown error"
					);
				}
			} catch (error) {
				console.error("Error fetching classrooms:", error.message);
			}
		};

		fetchClassrooms();
	}, [courseId]);

	if (!course) return <CourseDetailLoading />;

	return (
		<div className="min-h-screen p-8 bg-gray-100 text-gray-900">
			<div className="max-w-4xl mx-auto bg-white shadow-xl rounded-lg overflow-hidden">
				{/* Course Image */}
				<div>
					<video
						poster={course?.image || "./images/coding.jpg"}
						className="w-full h-full object-cover rounded-xl"
						src={course?.video || "/video/2.mp4"}
						loop={true}
						controls
					></video>
				</div>

				{/* Course Details */}
				<div className="p-8">
					{/* Course Title */}
					<h1 className="text-2xl sm:text-3xl font-bold text-blue-700 mb-4 sm:mb-6 text-center">
						{course.name}
					</h1>

					{/* Course Description */}
					<p className="sm:text-lg text-gray-700 mb-6">
						{course.description}
					</p>

					{/* Course Category and Price */}
					<div className="flex justify-between items-center mb-6">
						<div className="flex flex-col gap-1">
							<h2 className="sm:text-lg font-semibold text-gray-800">
								Category
							</h2>
							<p className="text-sm sm:text-base text-gray-600">
								{course?.category?.title || "N/A"}
							</p>
						</div>
						<div className="flex flex-col gap-1">
							<h2 className="sm:text-lg font-semibold text-gray-800">
								Price
							</h2>
							<p className="text-sm sm:text-base text-green-600 font-bold">
								$ {course.price}
							</p>
						</div>
					</div>

					{/* Teachers Section */}
					<div className="mb-6">
						<h2 className="sm:text-lg font-medium mb-1 sm:mb-4">
							Instructors
						</h2>
						{course.teachers && course.teachers.length > 0 ? (
							<div className="flex flex-wrap items-center gap-6">
								{course.teachers.map((teacher) => (
									<div
										key={teacher._id}
										className="flex items-center space-x-4 bg-indigo-50 p-4 rounded-lg shadow-md"
									>
										<img
											src={
												teacher.profilePic
													? // ? `${BASE_UPLOADS}/profile/${teacher._id}/${teacher.profilePic}`
													  teacher.profilePic
													: "./images/default-profile.jpg"
											}
											alt={teacher.fullName}
											className="w-16 h-16 rounded-full border-2 border-blue-600"
										/>
										<span className="sm:text-lg font-semibold text-gray-800">
											{teacher.fullName ||
												"Unnamed Instructor"}
										</span>
									</div>
								))}
							</div>
						) : (
							<p className="text-sm sm:text-base text-gray-600">
								No instructors available.
							</p>
						)}
					</div>

					{/* Schedule */}
					<div className="flex sm:flex-row flex-col justify-between sm:mb-6">
						<div className="mb-6">
							<h2 className="sm:text-lg font-semibold text-gray-800 mb-2">
								Schedule
							</h2>
							<ul className="list-disc pl-6 space-y-1 text-gray-700">
								{course.schedule.map((time, index) => (
									<li
										key={index}
										className="text-sm sm:text-base"
									>
										{time}
									</li>
								))}
							</ul>
						</div>
						<div className="mb-6">
							<h2 className="sm:text-lg font-semibold text-gray-800 mb-2">
								Session Time
							</h2>
							<ul className="list-disc pl-6 space-y-1 text-gray-700">
								{course.sessionTimes.map((time, index) => (
									<li
										key={index}
										className="text-sm sm:text-base"
									>
										{time}
									</li>
								))}
							</ul>
						</div>
						<div className="mb-6">
							<h2 className="sm:text-lg font-semibold text-gray-800 mb-2">
								Max Students/Class
							</h2>
							<p className="text-sm sm:text-base">
								{course.maxStudents}
							</p>
						</div>
					</div>

					{/* Duration */}
					<div className="mb-6">
						<h2 className="sm:text-lg font-semibold text-gray-800 mb-2">
							Duration
						</h2>
						<p className="text-gray-700 text-sm sm:text-base">
							{course.durationInMinutes} minutes
						</p>
					</div>

					{/* Prerequisites */}
					<div className="mb-6">
						<h2 className="sm:text-lg font-semibold text-gray-800 mb-2">
							Prerequisites
						</h2>
						<p className="text-gray-700 text-sm sm:text-base">
							{course?.prerequisiteCourses?.length
								? course.prerequisiteCourses.map(
										(prerequisite, index) => (
											<span key={index}>
												{prerequisite.name}
												{index <
													course.prerequisiteCourses
														.length -
														1 && (
													<span className="mx-2">
														•
													</span>
												)}
											</span>
										)
								  )
								: "None"}
						</p>
					</div>

					{/* Classrooms Section */}
					<div className="mb-8">
						<h2 className="sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-4">
							Select a Classroom
						</h2>
						{classrooms.length > 0 ? (
							<div>
								<select
									value={selectedClassroom}
									onChange={(e) =>
										setSelectedClassroom(e.target.value)
									}
									className="w-full p-2 border rounded-lg text-sm sm:text-base"
								>
									<option value="" disabled>
										-- Select a Classroom --
									</option>
									{classrooms.map((classroom) => (
										<option
											key={classroom._id}
											value={classroom._id}
											className="flex justify-between text-sm sm:text-base"
										>
											{`${
												(classroom.userId?.fullName &&
													`Inst. ${classroom.userId?.fullName}`) ||
												"Unknown Instructor"
											}    -    
											
												${
													(classroom.sessionTime &&
														`${classroom.sessionTime} UTC`) ||
													"Unknown Session time"
												}`}
										</option>
									))}
								</select>
							</div>
						) : (
							<p className="text-gray-600">
								No classrooms available for this course.
							</p>
						)}
					</div>

					<PaymentOptions
						course={course}
						isModalVisible={isModalVisible}
						setModalVisible={setModalVisible}
						selectedClassroom={selectedClassroom}
						user={user}
					/>

					{/* Enroll Button */}
					<div className="flex justify-center">
						<button
							onClick={() => {
								if (!selectedClassroom) {
									toast.error(
										"Please select a classroom before enrolling."
									);
									return;
								}
								setModalVisible(true);
							}}
							className="bg-blue-600 text-white sm:text-lg py-2 px-3 rounded-lg hover:bg-blue-700 transition-all duration-300 "
						>
							Enroll Now
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CourseDetailPage;
